import { Box } from "@mui/material";
import { useMemo } from "react";
import AddCollection from "../AddCollection/AddCollection";
import AddContent from "../AddContent/AddContent";
import { disabledBlockSx } from "../disabledStyles";
import PagePicker from "../PageListing/PagePicker";
import RadioGroupButtons from "../RadioGroup/RadioGroupButtons";

const COMPONENT_TYPE_OPTIONS = [
  { value: "page", label: "Page" },
  { value: "content_type", label: "Article" },
  // { value: "collection", label: "Collection" },
];

const wrapAsArray = (value: any) => (value ? [value] : []);
const unwrapSingle = (value: any) => (Array.isArray(value) ? value[0] : value);
const trimSelectedPage = (page: any) =>
  page && typeof page === "object"
    ? { Page: page.Page, Slug: page.Slug, Title: page.Title, CurrentPageUrl: page.CurrentPageUrl }
    : page;

const ComponentRender = ({
  field,
  state,
  setState,
  errors,
  register,
  clearErrors,
  control,
  isDisabled = false,
}: any) => {
  const referenceValue = state?.[field?.name] || {};
  const selectedType = referenceValue.selected_type || field?.default_value || "";

  const updateReference = (patch: any) => {
    setState({
      ...state,
      [field?.name]: { ...referenceValue, ...patch },
    });
  };

  const handleTypeChange = (event: any) => {
    clearErrors?.(field?.name);
    updateReference({ selected_type: event.target.value });
  };

  const schemaContent = Array.isArray(field?.allowed_content) ? field.allowed_content : [];
  const schemaCollection = Array.isArray(field?.allowed_collection) ? field.allowed_collection : [];
  // Backward compatible: schemas that list nothing keep the legacy Article picker.
  const allowedContent = schemaContent;
  const allowedCollection = schemaCollection;

  const typeOptions = COMPONENT_TYPE_OPTIONS?.filter(
    (option) =>
      option.value === "page" ||
      (option.value === "content_type" && allowedContent?.length > 0) ||
      (option.value === "collection" && allowedCollection?.length > 0),
  );

  const pageRegisterName = `${field?.name}_page`;
  const contentTypeRegisterName = `${field?.name}_content_type`;
  const collectionRegisterName = `${field?.name}_collection`;

  const scopedState = useMemo(
    () => ({
      [pageRegisterName]: referenceValue.selected_page,
      [contentTypeRegisterName]: wrapAsArray(referenceValue.selected_content_type),
      [collectionRegisterName]: wrapAsArray(referenceValue.selected_collection),
    }),
    [
      pageRegisterName,
      contentTypeRegisterName,
      collectionRegisterName,
      referenceValue.selected_page,
      referenceValue.selected_content_type,
      referenceValue.selected_collection,
    ],
  );

  const scopedSetState = (updated: any) => {
    updateReference({
      selected_page: trimSelectedPage(updated?.[pageRegisterName]),
      selected_content_type: unwrapSingle(updated?.[contentTypeRegisterName]),
      selected_collection: unwrapSingle(updated?.[collectionRegisterName]),
    });
  };

  const pageField = { ...field, name: pageRegisterName, title: "Choose Page" };
  const contentTypeField = {
    ...field,
    name: contentTypeRegisterName,
    title: "Choose Article",
    allowed_content: allowedContent,
    allowed_collection: [],
    multiple: false,
  };
  const collectionField = {
    ...field,
    name: collectionRegisterName,
    allowed_content: [],
    allowed_collection: allowedCollection,
    multiple: false,
  };

  return (
    <Box id={`${field?.name}_id`} sx={disabledBlockSx(isDisabled)}>
      <Box sx={{ mb: "15px" }}>
        <RadioGroupButtons
          value={typeOptions}
          field={field}
          row
          handleChange={handleTypeChange}
          error={errors?.[field?.name]}
          control={control}
          clearErrors={clearErrors}
          data={selectedType}
        />
      </Box>
      {selectedType === "page" && (
        <PagePicker state={scopedState} setState={scopedSetState} field={pageField} />
      )}
      {selectedType === "content_type" && (
        <AddContent
          state={scopedState}
          setState={scopedSetState}
          field={contentTypeField}
          error={errors?.[contentTypeRegisterName]}
          register={register}
          clearErrors={clearErrors}
        />
      )}
      {selectedType === "collection" && (
        <AddCollection
          state={scopedState}
          setState={scopedSetState}
          field={collectionField}
          error={errors?.[collectionRegisterName]}
          register={register}
          clearErrors={clearErrors}
        />
      )}
    </Box>
  );
};

export default ComponentRender;
